import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PendingDisclaimer, SessionEndReason, StaffUser } from '@/types/auth.types';
import type { AccountModule } from '@/services/accountModules.service';
import { setTzRules } from '@/lib/tzRules';

interface AuthState {
  token: string | null;
  user: StaffUser | null;
  accountId: number | null;
  pendingDisclaimers: PendingDisclaimer[];
  /**
   * True while this practice is waiting for its first account owner to accept
   * the owner agreement. Held users can sign in but reach nothing else.
   *
   * Kept beside `pendingDisclaimers` rather than on `user` on purpose: the
   * refresh in Layout feeds /auth/me through `updateUser`, which merges into
   * `user` only, so anything living there would be re-read on every mount and
   * silently discarded.
   */
  ownerAgreementHold: boolean;
  /**
   * Per-account module map from the login//auth/me payload. Persisted so the
   * sidebar can filter module-gated entries on first paint (restricted
   * accounts) without waiting for /account/modules; useAccountModules seeds
   * its query from this and keeps refetching as usual.
   */
  modules: AccountModule[] | null;
  /**
   * Why the session just ended, or null while it hasn't. Set by endSession()
   * right after logout(); SessionWatcher redirects on it. Memory only — never
   * persisted, or every later page load would redirect.
   */
  endReason: SessionEndReason | null;
}

interface AuthActions {
  login: (
    token: string,
    user: StaffUser,
    pendingDisclaimers?: PendingDisclaimer[],
    modules?: AccountModule[] | null,
    ownerAgreementHold?: boolean,
  ) => void;
  logout: () => void;
  updateUser: (updates: Partial<StaffUser>) => void;
  setModules: (modules: AccountModule[] | null) => void;
  setPendingDisclaimers: (items: PendingDisclaimer[]) => void;
  setOwnerAgreementHold: (held: boolean) => void;
  acknowledgeDisclaimer: (slug: string, code: string) => void;
  setEndReason: (reason: SessionEndReason) => void;
}

export type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  token: null,
  user: null,
  accountId: null,
  pendingDisclaimers: [],
  ownerAgreementHold: false,
  modules: null,
  endReason: null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      login: (token, user, pendingDisclaimers = [], modules = null, ownerAgreementHold = false) =>
        set({
          token,
          user,
          accountId: user.accountId,
          pendingDisclaimers,
          ownerAgreementHold,
          modules,
        }),

      logout: () => set(initialState),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      setModules: (modules) => set({ modules }),

      setPendingDisclaimers: (items) => set({ pendingDisclaimers: items }),

      setOwnerAgreementHold: (held) => set({ ownerAgreementHold: held }),

      acknowledgeDisclaimer: (slug, code) =>
        set((state) => ({
          pendingDisclaimers: state.pendingDisclaimers.filter(
            (d) => !(d.slug === slug && d.code === code),
          ),
        })),

      setEndReason: (reason) => set({ endReason: reason }),
    }),
    {
      name: 'ftp-auth',
      storage: createJSONStorage(() => localStorage),
      // Persist token, user, the pending-disclaimer queue and the owner hold so
      // a hard refresh while either gate is up does not allow bypass.
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        accountId: state.accountId,
        pendingDisclaimers: state.pendingDisclaimers,
        ownerAgreementHold: state.ownerAgreementHold,
        modules: state.modules,
      }),
    },
  ),
);

// Keep the time conversions on the signed-in user's server rules
// (SCRUM-2428). localStorage rehydrates during create(), so this first read
// already has the stored rules before any page renders; the subscription
// follows sign-in, the /auth/me refresh, a timezone change and sign-out.
setTzRules(useAuthStore.getState().user?.tzRules);
useAuthStore.subscribe((state) => setTzRules(state.user?.tzRules));
